import React from 'react'
import MetricCard from '../MetricCard/MetricCard.component'
import type { SectionCardProps } from './SectionCard.types'
import { SECTION_CARD_DEMO_TITLE } from './SectionCard.types'
import './SectionCard.css'

const SectionCard: React.FC<SectionCardProps> = ({
  children,
  title,
  titleHref,
  showTitleChevron = true,
  headerAfterTitle,
  headerRight,
  radius = 'lg',
  contentLayout = 'column',
  className = '',
  darkMode = false,
  previewState,
}) => {
  const isPreview = previewState != null
  const resolvedTitle =
    title ?? (isPreview ? SECTION_CARD_DEMO_TITLE : undefined)

  const resolvedTitleHref =
    titleHref ??
    (previewState === 'with-link' || previewState === 'with-header-actions'
      ? '#section-card-demo'
      : undefined)

  const resolvedHeaderAfterTitle =
    headerAfterTitle ??
    (previewState === 'with-header-actions' ? (
      <button
        type="button"
        className="ds-section-card__icon-button"
        tabIndex={-1}
        aria-label="More information"
      >
        <i className="mgc mgc_information_fill" aria-hidden="true" />
      </button>
    ) : undefined)

  const resolvedHeaderRight =
    headerRight ??
    (previewState === 'with-header-actions' ? (
      <button
        type="button"
        className="ds-section-card__icon-button"
        tabIndex={-1}
        aria-label="Open menu"
      >
        <i className="mgc mgc_more_2_fill" aria-hidden="true" />
      </button>
    ) : undefined)

  const showHeader = Boolean(
    resolvedTitle || resolvedHeaderAfterTitle || resolvedHeaderRight,
  )

  const titleContent = resolvedTitle ? (
    <>
      <h3 className="ds-section-card__title">{resolvedTitle}</h3>
      {showTitleChevron && resolvedTitleHref ? (
        <i
          className="mgc mgc_right_line ds-section-card__title-chevron"
          aria-hidden="true"
        />
      ) : null}
    </>
  ) : null

  const resolvedContentLayout =
    previewState === 'with-metrics' ? 'row' : contentLayout

  const resolvedChildren =
    children ??
    (previewState === 'with-metrics' ? (
      <>
        <MetricCard
          label="Collected"
          value="$18,420"
          footer="8% vs last week"
          trend="positive"
          darkMode={darkMode}
        />
        <MetricCard
          label="Outstanding"
          value="$6,110"
          footer="3% vs last week"
          trend="negative"
          darkMode={darkMode}
        />
        <MetricCard
          label="Collection rate"
          value="75%"
          footer="On track"
          trend="neutral"
          darkMode={darkMode}
        />
      </>
    ) : isPreview ? (
      <div className="ds-section-card__body-placeholder">Chart / content</div>
    ) : null)

  const classNames = [
    'ds-section-card',
    `ds-section-card--radius-${radius}`,
    `ds-section-card--content-${resolvedContentLayout}`,
    darkMode ? 'ds-section-card--dark' : '',
    isPreview ? 'ds-section-card--preview' : '',
    previewState ? `ds-section-card--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <section className={classNames}>
      {showHeader ? (
        <div className="ds-section-card__header">
          <div className="ds-section-card__header-start">
            {titleContent ? (
              resolvedTitleHref ? (
                <a
                  href={resolvedTitleHref}
                  className="ds-section-card__title-link"
                  onClick={
                    isPreview
                      ? (event) => {
                          event.preventDefault()
                        }
                      : undefined
                  }
                >
                  {titleContent}
                </a>
              ) : (
                titleContent
              )
            ) : null}
            {resolvedHeaderAfterTitle ? (
              <div className="ds-section-card__header-after-title">
                {resolvedHeaderAfterTitle}
              </div>
            ) : null}
          </div>
          {resolvedHeaderRight ? (
            <div className="ds-section-card__header-end">
              {resolvedHeaderRight}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="ds-section-card__content">{resolvedChildren}</div>
    </section>
  )
}

export default SectionCard
